import type { Observer, Unsubscribable } from 'type-fest';

import { noop } from './noop';

export const noopUnsubscribe = { unsubscribe: noop };

//Monkeypatch queueMicrotask when not available
//* It is available in most modern browsers: https://caniuse.com/?search=queuemicrotask, so this monkeypatch is rarely defined
//* https://developer.mozilla.org/en-US/docs/Web/API/queueMicrotask#when_queuemicrotask_isnt_available
const _globalThis = globalThis || self || window || this;
if (typeof _globalThis.queueMicrotask !== 'function') {
  _globalThis.queueMicrotask = function (callback) {
    Promise.resolve()
      .then(callback)
      .catch((e) =>
        setTimeout(() => {
          throw e;
        }),
      ); // report exceptions
  };
}

export const removeObserverSubscription = <T>(
  subscription: Partial<Observer<T>>,
  subscriptions: Iterable<Partial<Observer<T>>>,
): boolean => {
  if (subscriptions instanceof Set) {
    return subscriptions.delete(subscription); //true if removed
  } else if (Array.isArray(subscriptions)) {
    const index = subscriptions.findIndex((s) => s === subscription);
    if (index > -1) {
      subscriptions.splice(index, 1);
      return true; //removed
    }
  }
  return false; //not removed
};

//Helpers for calling observer fns (error, next, end) from a subscription to an Observable-Like
//* See https://github.com/sindresorhus/type-fest/blob/main/source/observable-like.d.ts
const callErrorObserver = <T>(
  subscription: Partial<Observer<T>>,
  err: unknown,
  subscriptions: Iterable<Partial<Observer<T>>>,
) => {
  const error = subscription?.error;
  if (typeof error === 'function') {
    try {
      error(err);
    } catch {
      //loose right to subscription if error() throws
      removeObserverSubscription(subscription, subscriptions);
    }
  }
};

export const callNextObservers = <T>(
  subscriptions: Iterable<Partial<Observer<T>>>,
  value: T,
) => {
  for (const subscription of subscriptions) {
    const next = subscription?.next;
    if (typeof next === 'function') {
      queueMicrotask(() => {
        try {
          next(value);
        } catch (err) {
          callErrorObserver(subscription, err, subscriptions);
          //loose right to subscription if next() throws
          removeObserverSubscription(subscription, subscriptions);
        }
      });
    }
  }
};

export const callCompleteObservers = <T>(
  subscriptions: Iterable<Partial<Observer<T>>>,
) => {
  for (const subscription of subscriptions) {
    const complete = subscription?.complete;
    if (typeof complete === 'function') {
      queueMicrotask(() => {
        try {
          complete();
        } catch (err) {
          callErrorObserver(subscription, err, subscriptions);
          //loose right to subscription if complete() throws
          removeObserverSubscription(subscription, subscriptions);
        }
      });
    }
  }
};

export const callNextAndCompleteObservers = <T>(
  subscriptions: Iterable<Partial<Observer<T>>>,
  value: T,
) => {
  for (const subscription of subscriptions) {
    const next = subscription?.next;
    if (typeof next === 'function') {
      queueMicrotask(() => {
        try {
          next(value);
        } catch (err) {
          callErrorObserver(subscription, err, subscriptions);
          //loose right to subscription if next() throws
          removeObserverSubscription(subscription, subscriptions);
        }
      });
    }
    const complete = subscription?.complete;
    if (typeof complete === 'function') {
      queueMicrotask(() => {
        try {
          complete();
        } catch (err) {
          callErrorObserver(subscription, err, subscriptions);
          //loose right to subscription if complete() throws
          removeObserverSubscription(subscription, subscriptions);
        }
      });
    }
  }
};

export const callErrorObservers = <T>(
  subscriptions: Iterable<Partial<Observer<T>>>,
  err: unknown,
) => {
  for (const subscription of subscriptions) {
    const error = subscription?.error;
    if (typeof error === 'function') {
      queueMicrotask(() => {
        try {
          error(err);
        } catch {
          //loose right to subscription if error() throws
          removeObserverSubscription(subscription, subscriptions);
        }
      });
    }
  }
};

//Helper to create Observable-Like's subscribe() method
//Returns `Unsubscribable`. See https://github.com/sindresorhus/type-fest/blob/main/source/observable-like.d.ts
export const subscribeObserver = <T>(
  observer: Partial<Observer<T>>,
  subscriptions: Set<Partial<Observer<T>>>,
  isActive = true,
): Unsubscribable => {
  if (isActive && observer) {
    subscriptions.add(observer);
    return {
      unsubscribe: () => {
        subscriptions.delete(observer);
      },
    };
  }
  return noopUnsubscribe;
};
